import { cn } from '@/lib/utils';

export type MobileSidePanelTheme = 'navbar' | 'enterprise';

export const MOBILE_NAV_ICON_SLOT =
  'flex size-8 shrink-0 items-center justify-center';

export const MOBILE_NAV_ROW =
  'flex min-h-9 h-9 w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-lg px-2 text-sm font-medium touch-manipulation transition-colors';

export const MOBILE_DROPDOWN_CHILD_ROW =
  'flex min-h-7 w-full cursor-pointer items-center gap-2 px-2 py-0.5 text-sm leading-tight touch-manipulation transition-colors';

/** Expanded dropdown / account-settings child list — tight rows + hairline dividers. */
export function mobileDropdownChildListClass(
  theme: MobileSidePanelTheme,
  isRTL: boolean,
): string {
  return cn(
    'mt-0 flex flex-col divide-y',
    MOBILE_DROPDOWN_CHILD_INDENT,
    isRTL ? 'border-r pe-2' : 'border-l',
    theme === 'enterprise'
      ? 'divide-emerald-800/50 border-emerald-700/60'
      : 'divide-gray-200/80 border-gray-200 dark:divide-gray-700/55 dark:border-gray-700',
  );
}

/** Child indent for expandable nav groups (icons use MOBILE_NAV_ICON_SLOT). */
export const MOBILE_DROPDOWN_CHILD_INDENT = 'ps-8';

export function mobileAdminRowClass(theme: MobileSidePanelTheme, active: boolean): string {
  if (theme === 'enterprise') {
    return cn(
      MOBILE_NAV_ROW,
      active
        ? 'border-amber-400/45 bg-amber-500/20 text-amber-50 shadow-sm shadow-emerald-950/40'
        : 'border-transparent text-amber-200/90 hover:bg-amber-950/35 hover:text-amber-50',
    );
  }

  return cn(
    MOBILE_NAV_ROW,
    active
      ? 'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
      : 'text-yellow-700 hover:bg-yellow-50 dark:text-yellow-300 dark:hover:bg-yellow-900/20',
  );
}

export function mobileNavRowClass(
  theme: MobileSidePanelTheme,
  active: boolean,
  options?: {
    isBilling?: boolean;
    isSubscriptionView?: boolean;
    hasPaid?: boolean;
    usageTabColor?: 'red' | 'yellow' | null;
    isSuperAdminTab?: boolean;
  },
): string {
  const { isBilling, isSubscriptionView, hasPaid, usageTabColor, isSuperAdminTab } = options ?? {};

  if (isSuperAdminTab) {
    return mobileAdminRowClass(theme, active);
  }

  if (isBilling && isSubscriptionView && !hasPaid) {
    if (usageTabColor === 'red') {
      return cn(
        MOBILE_NAV_ROW,
        active
          ? theme === 'enterprise'
            ? 'border-red-400/50 bg-red-500/25 text-red-50'
            : 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300'
          : theme === 'enterprise'
            ? 'border-transparent text-red-200 hover:bg-red-950/40'
            : 'text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/30',
      );
    }
    if (usageTabColor === 'yellow') {
      return cn(
        MOBILE_NAV_ROW,
        active
          ? theme === 'enterprise'
            ? 'border-amber-400/50 bg-amber-500/20 text-amber-50'
            : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
          : theme === 'enterprise'
            ? 'border-transparent text-amber-100 hover:bg-amber-950/35'
            : 'text-yellow-600 hover:bg-yellow-50 dark:text-yellow-300 dark:hover:bg-yellow-900/30',
      );
    }
  }

  if (theme === 'enterprise') {
    return cn(
      MOBILE_NAV_ROW,
      active
        ? 'border-emerald-400/50 bg-emerald-500/25 text-white shadow-sm shadow-emerald-950/40'
        : 'border-transparent text-emerald-50/95 hover:bg-emerald-800/50 hover:text-white',
    );
  }

  return cn(
    MOBILE_NAV_ROW,
    active
      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800',
  );
}

export function mobileDropdownParentClass(theme: MobileSidePanelTheme, active: boolean): string {
  if (theme === 'enterprise') {
    return cn(
      MOBILE_NAV_ROW,
      active
        ? 'border-emerald-400/50 bg-emerald-500/25 text-white'
        : 'border-transparent text-emerald-50/95 hover:bg-emerald-800/50 hover:text-white',
    );
  }

  return cn(
    MOBILE_NAV_ROW,
    active
      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800',
  );
}

export function mobileDropdownChildClass(theme: MobileSidePanelTheme, active: boolean): string {
  if (theme === 'enterprise') {
    return active
      ? 'bg-emerald-500/20 font-medium text-white'
      : 'text-emerald-100/90 hover:bg-emerald-800/40 hover:text-white';
  }

  return active
    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800';
}

export function mobileNavIconClass(theme: MobileSidePanelTheme, active: boolean): string {
  if (theme === 'enterprise') {
    return active ? 'text-white' : 'text-emerald-100';
  }

  return active ? 'text-green-700 dark:text-green-300' : 'text-gray-600 dark:text-gray-300';
}

export function mobileSectionDividerClass(theme: MobileSidePanelTheme): string {
  return theme === 'enterprise'
    ? 'my-1 border-t border-emerald-800/60'
    : 'my-2 border-t border-gray-200 dark:border-gray-800';
}
